import { nip19 } from 'nostr-tools';
import { useParams, Link } from 'react-router-dom';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { NoteContent } from '@/components/NoteContent';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, ExternalLink, Copy } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import type { NostrEvent } from '@nostrify/nostrify';
import NotFound from './NotFound';

// ─── Profile Page ──────────────────────────────────────────────────────────────

function ProfilePage({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const { toast } = useToast();
  const npub = nip19.npubEncode(pubkey);
  // Event-sourced URLs — sanitize before they land in href/src attributes.
  const safeBanner = sanitizeUrl(metadata?.banner);
  const safeWebsite = sanitizeUrl(metadata?.website);

  const copyNpub = () => {
    navigator.clipboard.writeText(npub);
    toast({ title: 'Copied npub to clipboard' });
  };

  if (author.isLoading) {
    return (
      <div className="max-w-2xl mx-auto pt-8 px-4">
        <Skeleton className="h-40 w-full rounded-xl mb-4" />
        <div className="space-y-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Banner */}
      <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20 relative">
        {safeBanner && (
          <img src={safeBanner} alt="" className="w-full h-full object-cover" />
        )}
      </div>

      <div className="px-4 sm:px-6">
        {/* Avatar */}
        <div className="-mt-12 mb-4">
          <Avatar className="w-24 h-24 border-4 border-background">
            <AvatarImage src={metadata?.picture} alt={displayName} />
            <AvatarFallback className="text-3xl">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
        </div>

        <div className="space-y-4 pb-6">
          <div>
            <h1 className="text-2xl font-bold">{displayName}</h1>
            {metadata?.nip05 && (
              <p className="text-sm text-muted-foreground">{metadata.nip05}</p>
            )}
          </div>

          {metadata?.about && (
            <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed">{metadata.about}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={copyNpub}>
              <Copy className="w-3.5 h-3.5" />
              Copy npub
            </Button>
            {safeWebsite && (
              <Button size="sm" variant="outline" className="gap-1.5" asChild>
                <a href={safeWebsite} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-3.5 h-3.5" />
                  Website
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Recent notes */}
        <RecentNotes pubkey={pubkey} />
      </div>
    </div>
  );
}

function RecentNotes({ pubkey }: { pubkey: string }) {
  const { nostr } = useNostr();
  const { data: events, isLoading } = useQuery({
    queryKey: ['profile-notes', pubkey],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [1], authors: [pubkey], limit: 20 }],
        { signal }
      );
      return events.sort((a, b) => b.created_at - a.created_at);
    },
    staleTime: 60000,
  });

  if (isLoading) {
    return (
      <div className="space-y-0 border-t border-border/50">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="py-4 border-b border-border/40">
            <Skeleton className="h-3 w-full mb-1.5" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="border-t border-border/50 py-8 text-center text-sm text-muted-foreground">
        No recent notes found.
      </div>
    );
  }

  return (
    <div className="border-t border-border/50">
      <p className="py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        Recent Notes
      </p>
      {events.map((event: NostrEvent) => {
        const nevent = nip19.neventEncode({ id: event.id, author: pubkey });
        return (
          <Link
            key={event.id}
            to={`/${nevent}`}
            className="block py-3 border-b border-border/40 hover:bg-muted/20 transition-colors rounded -mx-1 px-1"
          >
            <NoteContent
              event={event}
              className="text-sm leading-relaxed"
              disableNoteEmbeds
              disableMediaEmbeds={false}
            />
            <span className="text-[10px] text-muted-foreground mt-1 block">
              {new Date(event.created_at * 1000).toLocaleString()}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

// ─── Event / Note Page ─────────────────────────────────────────────────────────

function EventPage({ id, authorPubkey }: { id: string; authorPubkey?: string }) {
  const { nostr } = useNostr();

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: async ({ signal }) => {
      const filter = authorPubkey
        ? { ids: [id], authors: [authorPubkey], limit: 1 }
        : { ids: [id], limit: 1 };
      const results = await nostr.query([filter], { signal });
      return results[0] ?? null;
    },
    staleTime: 300000,
  });

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto pt-8 px-4 space-y-4">
        <div className="flex gap-3 items-start">
          <Skeleton className="w-10 h-10 rounded-full shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="max-w-2xl mx-auto pt-16 px-4 text-center text-muted-foreground">
        <p>Event not found. It may not be available on the connected relays.</p>
      </div>
    );
  }

  return <EventDetail event={event} />;
}

function EventDetail({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const meta = author.data?.metadata;
  const displayName = meta?.name ?? meta?.display_name ?? genUserName(event.pubkey);
  const npub = nip19.npubEncode(event.pubkey);
  const { nostr } = useNostr();

  const { data: replies, isLoading: repliesLoading } = useQuery({
    queryKey: ['event-replies', event.id],
    queryFn: async ({ signal }) => {
      const results = await nostr.query(
        [{ kinds: [1], '#e': [event.id], limit: 50 }],
        { signal }
      );
      return results
        .filter((e) => e.tags.some(([t, id]) => t === 'e' && id === event.id))
        .sort((a, b) => a.created_at - b.created_at);
    },
    staleTime: 30000,
  });

  return (
    <div className="max-w-2xl mx-auto px-4 pb-8">
      {/* Focused event */}
      <div className="py-5 border-b border-border/50">
        <div className="flex gap-3 items-start">
          <Link to={`/${npub}`} className="shrink-0">
            <Avatar className="w-11 h-11">
              <AvatarImage src={meta?.picture} alt={displayName} />
              <AvatarFallback className="text-sm">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1.5 mb-1 flex-wrap">
              <Link to={`/${npub}`} className="font-semibold text-sm hover:underline">{displayName}</Link>
              {meta?.nip05 && (
                <span className="text-xs text-muted-foreground">{meta.nip05.replace(/^_@/, '')}</span>
              )}
            </div>
            <NoteContent
              event={event}
              className="text-base leading-relaxed"
              disableNoteEmbeds={false}
              disableMediaEmbeds={false}
            />
            <p className="text-xs text-muted-foreground mt-2">
              {new Date(event.created_at * 1000).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Replies */}
      <div className="pt-2">
        {repliesLoading ? (
          <div className="space-y-0">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="py-3 border-b border-border/40 flex gap-3">
                <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : replies && replies.length > 0 ? (
          <>
            <p className="py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
            </p>
            {replies.map((reply: NostrEvent) => {
              const replyNevent = nip19.neventEncode({ id: reply.id, author: reply.pubkey });
              return (
                <Link
                  key={reply.id}
                  to={`/${replyNevent}`}
                  className="block py-3 border-b border-border/40 hover:bg-muted/20 transition-colors"
                >
                  <ReplyRow event={reply} />
                </Link>
              );
            })}
          </>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">No replies yet.</p>
        )}
      </div>
    </div>
  );
}

function ReplyRow({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const meta = author.data?.metadata;
  const displayName = meta?.name ?? meta?.display_name ?? genUserName(event.pubkey);

  return (
    <div className="flex gap-2.5">
      <Avatar className="w-8 h-8 shrink-0">
        <AvatarImage src={meta?.picture} alt={displayName} />
        <AvatarFallback className="text-xs">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm mb-0.5">{displayName}</p>
        <NoteContent
          event={event}
          className="text-sm leading-relaxed"
          disableNoteEmbeds
          disableMediaEmbeds={false}
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          {new Date(event.created_at * 1000).toLocaleString()}
        </p>
      </div>
    </div>
  );
}

// ─── Page wrapper ──────────────────────────────────────────────────────────────

export function NIP19Page() {
  const { nip19: identifier } = useParams<{ nip19: string }>();

  if (!identifier) return <NotFound />;

  let decoded;
  try {
    decoded = nip19.decode(identifier);
  } catch {
    return <NotFound />;
  }

  const { type } = decoded;

  const content = (() => {
    switch (type) {
      case 'npub':
        return <ProfilePage pubkey={decoded.data as string} />;

      case 'nprofile':
        return <ProfilePage pubkey={(decoded.data as { pubkey: string }).pubkey} />;

      case 'note':
        return <EventPage id={decoded.data as string} />;

      case 'nevent': {
        const d = decoded.data as { id: string; author?: string };
        return <EventPage id={d.id} authorPubkey={d.author} />;
      }

      case 'naddr':
        // Addressable events — redirect to desktop for now
        return (
          <div className="text-center py-8 text-muted-foreground">
            <p className="mb-4">Addressable event. Open in nostrVM desktop to view.</p>
            <Button asChild variant="outline">
              <Link to="/">Open nostrVM</Link>
            </Button>
          </div>
        );

      default:
        return <NotFound />;
    }
  })();

  return (
    <div className="min-h-screen bg-background">
      {/* Minimal nav bar */}
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/90 backdrop-blur">
        <div className="max-w-2xl mx-auto px-4 h-12 flex items-center gap-3">
          <Button size="sm" variant="ghost" className="gap-1.5 -ml-2" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4" />
              nostrVM
            </Link>
          </Button>
        </div>
      </header>
      <main>{content}</main>
    </div>
  );
}
